import React, { useRef } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { InputSectionDiv, ProfileSection, InputDiv, DummySpan, TagInput, TagIconDiv } from './styles';
import Chip from '../../chipComponent';
import { CHIP_ICON_POSITION, CHIP_SIZE, CHIP_TYPE, ICON_SIZE } from '../../../global/constants';
import { MultiSelectInputComponentProps } from '../../../interfaces/inputInterface';
import { isEmpty } from '../../../helpers/common';

/**
 * MultiSelectInputComponent props
 * @typedef {Object} MultiSelectInputComponentProps
 * @property {boolean} [isDisabled] - Whether the multi-select input is disabled
 * @property {boolean} [isActive] - Whether the component is in active state
 * @property {(e: React.MouseEvent<HTMLDivElement>) => void} [onContainerClick] - Click handler for the container
 * @property {boolean} [isDropdownOpen] - Whether the dropdown is open
 * @property {Array<{id?: string | number, displayName: string, profile_photo?: string}>}
 *   [selectedUsers] - Array of selected users with display info
 * @property {(user, e?: React.SyntheticEvent) => void} [onRemoveUser] - Handler for removing a user from selection
 * @property {string} value - Current input value
 * @property {string} [placeholderWhenEmpty] - Placeholder text when no users are selected
 * @property {(e: React.ChangeEvent<HTMLInputElement>) => void} [onChange] - Change handler for input value
 * @property {(e: React.KeyboardEvent<HTMLInputElement>) => void} [onKeyDown] - Key down handler for input
 * @property {() => void} [onFocus] - Focus handler for input
 * @property {() => void} [onBlur] - Blur handler for input
 * @property {React.RefObject<HTMLInputElement>} [inputRef] - Ref for the input element
 * @property {boolean} [showIcon] - Whether to show the dropdown icon
 * @property {(e: React.MouseEvent<HTMLDivElement>) => void} [onIconClick] - Click handler for the icon
 * @property {string} [iconName] - Name of the icon to display
 * @property {string} [iconViewBox] - ViewBox for the icon SVG
 */

const MultiSelectInput: React.FC<MultiSelectInputComponentProps> = ({
  isActive = false,
  onContainerClick,
  isDropdownOpen = false,
  selectedUsers = [],
  onRemoveUser,
  value,
  placeholderWhenEmpty = 'Select members',
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  onIconClick,
  iconName = 'down-arrow-icon',
  iconViewBox = '0 0 18 18',
  inputRef,
  showIcon,
  isDisabled = false
}) => {
  // ref variables
  const internalInputRef = useRef<HTMLInputElement>(null);
  const actualInputRef = inputRef || internalInputRef;
  // other variables
  const hasUsers = selectedUsers && selectedUsers.length > 0;
  const displayPlaceholder = hasUsers ? '' : placeholderWhenEmpty;
  const dummySpanContent = hasUsers ? `${value}|` : `${placeholderWhenEmpty}|`;

  // render profile tags
  const renderProfileTag = () => {
    return selectedUsers?.map((user, index) => {
      const showAvatar = !isEmpty(user.profile_photo) && !isEmpty(user.displayName);
      const showProjectAvatar = !!user.projectIcon;
      return (
        <Chip
          key={user.id ?? `${user.displayName}-${index}`}
          avatarImgSrc={showAvatar ? user.profile_photo : undefined}
          avatarName={showAvatar ? user.displayName : undefined}
          projectIcon={showProjectAvatar ? user.projectIcon : undefined}
          projectName={showProjectAvatar ? user.displayName : undefined}
          title={user.displayName}
          variant={CHIP_TYPE.FILL}
          size={CHIP_SIZE.SMALL}
          iconPosition={
            showAvatar || showProjectAvatar
              ? CHIP_ICON_POSITION.AVATAR_TEXT_RIGHT_BUTTON
              : CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON
          }
          trailingIconButtonTitle='new-close-icon'
          onTrailingIconButtonClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            onRemoveUser?.(user, e);
            e.stopPropagation();
          }}
          isInputField={true}
        />
      );
    });
  };

  return (
    <InputSectionDiv
      role='button'
      selectedUsers={hasUsers}
      isActive={isActive}
      onClick={!isDisabled ? onContainerClick : undefined}>
      <ProfileSection>
        {renderProfileTag()}
        <InputDiv>
          <DummySpan isDisabled={isDisabled}>{dummySpanContent}</DummySpan>
          <TagInput
            ref={actualInputRef}
            placeholder={displayPlaceholder}
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={isDisabled}
          />
        </InputDiv>
      </ProfileSection>
      {showIcon && (
        <TagIconDiv role='button' onClick={!isDisabled ? onIconClick : undefined}>
          <SVGIcon
            name={iconName}
            width={ICON_SIZE.LG}
            height={ICON_SIZE.LG}
            viewBox={iconViewBox}
            fill={isDisabled ? 'var(--text-muted)' : 'var(--text-primary)'}
            className={`angleDown-icon ${isDropdownOpen ? 'rotate' : ''}`}
          />
        </TagIconDiv>
      )}
    </InputSectionDiv>
  );
};

export default MultiSelectInput;
